import {
  collection,
  deleteField,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger';

const logger = createLogger('paymentInventory');

/**
 * Cart line items still needing inventory decrement after prior partial progress.
 * @param {Array<{ productId: string, quantity?: number }>} cartItems
 * @param {Array<{ productId: string, quantity: number }>} [progress]
 */
export function cartItemsRemainingForDecrement(cartItems, progress = []) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return [];
  }
  if (!Array.isArray(progress) || progress.length === 0) {
    return cartItems;
  }

  const decrementedByProduct = new Map();
  for (const { productId, quantity } of progress) {
    if (!productId) continue;
    decrementedByProduct.set(productId, (decrementedByProduct.get(productId) || 0) + (quantity || 0));
  }

  const remaining = [];
  for (const item of cartItems) {
    const needed = item.quantity || 0;
    const done = decrementedByProduct.get(item.productId) || 0;
    const left = needed - done;
    if (left > 0) {
      remaining.push({ ...item, quantity: left });
    }
  }
  return remaining;
}

/** @param {Array<{ productId: string, quantity: number }>} [existing] */
export function mergeDecrementProgress(existing = [], newlyDecremented = []) {
  const merged = new Map();
  for (const { productId, quantity } of [...existing, ...newlyDecremented]) {
    if (!productId) continue;
    merged.set(productId, (merged.get(productId) || 0) + (quantity || 0));
  }
  return Array.from(merged.entries()).map(([productId, quantity]) => ({ productId, quantity }));
}

/**
 * Fields to persist on a transaction after a partial inventory decrement failure.
 * Customer clients cannot restore inventory (Firestore rules allow decrement-only);
 * resume from {@code inventoryDecrementProgress} on retry instead.
 */
export function buildPartialDecrementFailureUpdate(existingProgress, decrementedItems) {
  return {
    inventoryDecremented: false,
    inventoryDecrementProgress: mergeDecrementProgress(existingProgress, decrementedItems),
    updatedAt: serverTimestamp(),
  };
}

/** Fields to clear partial-decrement resume state after a successful decrement. */
export function buildDecrementSuccessUpdate() {
  return {
    inventoryDecrementProgress: deleteField(),
    updatedAt: serverTimestamp(),
  };
}

/**
 * Atomically claim inventory decrement for this transaction (idempotent across retries).
 * @returns {Promise<boolean>} true when this caller should run decrementInventory
 */
export async function tryClaimInventoryDecrement(transactionRef) {
  try {
    return await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(transactionRef);
      if (snap.exists() && snap.data()?.inventoryDecremented === true) {
        return false;
      }
      transaction.set(
        transactionRef,
        { inventoryDecremented: true, updatedAt: serverTimestamp() },
        { merge: true }
      );
      return true;
    });
  } catch (error) {
    logger.error('[paymentInventory] Failed to claim inventory decrement:', error);
    return false;
  }
}

async function adjustInventoryQuantity(supermarketId, productId, quantityDelta) {
  const inventoryQuery = query(
    collection(db, 'Inventory'),
    where('supermarket_id', '==', supermarketId),
    where('product_id', '==', productId),
    limit(1)
  );
  const snapshot = await getDocs(inventoryQuery);
  if (snapshot.empty) {
    return null;
  }

  const inventoryDoc = snapshot.docs[0];
  const data = inventoryDoc.data();
  const oldQty = data.quantity || 0;
  const newQty = oldQty + quantityDelta;

  if (newQty < 0) {
    return { inventoryDoc, oldQty, newQty, applied: false };
  }

  await updateDoc(inventoryDoc.ref, { quantity: newQty });
  return { inventoryDoc, oldQty, newQty, applied: true };
}

/** @returns {Promise<{ allSucceeded: boolean, decrementedItems: Array<{ productId: string, quantity: number }> }>} */
export async function decrementInventory(supermarketId, cartItems) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return { allSucceeded: false, decrementedItems: [] };
  }

  let allSucceeded = true;
  const decrementedItems = [];

  for (const product of cartItems) {
    try {
      const quantity = product.quantity || 0;
      const result = await adjustInventoryQuantity(
        supermarketId,
        product.productId,
        -quantity
      );

      if (!result) {
        logger.warn(
          `[paymentInventory] No inventory document found for product ${product.productId} in supermarket ${supermarketId}`
        );
        allSucceeded = false;
        continue;
      }

      if (!result.applied) {
        logger.warn(
          `[paymentInventory] Cannot update inventory for product ${product.productId}: would result in negative quantity (${result.newQty})`
        );
        allSucceeded = false;
        continue;
      }

      decrementedItems.push({ productId: product.productId, quantity });
    } catch (error) {
      logger.error(`[paymentInventory] Error updating inventory for product ${product.productId}:`, error);
      allSucceeded = false;
    }
  }

  return { allSucceeded, decrementedItems };
}

export async function restoreInventoryDecrements(supermarketId, decrementedItems) {
  for (const { productId, quantity } of decrementedItems) {
    try {
      await adjustInventoryQuantity(supermarketId, productId, quantity);
    } catch (error) {
      logger.error(`[paymentInventory] Error restoring inventory for product ${productId}:`, error);
    }
  }
}
